import { IPage } from '@aon/aon-tws-common-ui';
import { Action, Reducer } from 'redux';
import { IApplicationState, TThunkAction } from '..';
import { IFilterable } from '../../components/reporting/GroupAccessOwners/QueryableTableView';
import { IGroupAccessOwnerColumn } from '../../models/GroupAccessOwners/GroupAccessOwnerColumn';
import { IPerson } from '../../models/GroupAccessOwners/PersonVm';
import { IQueryOptions } from '../../models/GroupAccessOwners/ServiceInterfaces';
import { TFetchableEntity } from '../../models/IFetchableEntity';

export interface IGroupAccessOwnerViews {
	[forView: string]: IGroupAccessOwnerViewState;
}

export interface IGroupAccessOwnerViewState {
	columns: TFetchableEntity<IGroupAccessOwnerColumn[]>;
	filterables: TFetchableEntity<IFilterable[]>;
	people: TFetchableEntity<IPage<IPerson>>;
}

export function FetchStateForView(state: IApplicationState, forView: string): IGroupAccessOwnerViewState {
	return state.GroupAccessOwnerViews[forView] ?? DefaultViewState;
}

export const DefaultViewState: IGroupAccessOwnerViewState = {
	columns: { isLoading: true },
	filterables: { isLoading: true },
	people: { isLoading: true },
};

export enum GroupAccessOwnerViewActionTypes {
	COLUMN_CONSTRUCTION_STARTED = 'COLUMN_CONSTRUCTION_STARTED',
	COLUMN_CONSTRUCTION_FINISHED = 'COLUMN_CONSTRUCTION_FINISHED',
	COLUMN_CONSTRUCTION_FAILED = 'COLUMN_CONSTRUCTION_FAILED',

	FILTERABLE_CONSTRUCTION_STARTED = 'FILTERABLE_CONSTRUCTION_STARTED',
	FILTERABLE_CONSTRUCTION_FINISHED = 'FILTERABLE_CONSTRUCTION_FINISHED',

	REQUEST_PEOPLE = 'REQUEST_PEOPLE',
	PEOPLE_FETCHED = 'PEOPLE_FETCHED',
	PEOPLE_COULDNOT_BEFETCHED = 'PEOPLE_COULDNOT_BEFETCHED',

	NO_OP = 'NO_OP',
}

export type GroupAccessOwnerViewActions =
	| GroupAccessOwnerViewPayloadActions
	| (IBaseAction & { type: Exclude<GroupAccessOwnerViewActionTypes, GroupAccessOwnerViewPayloadActions['type']> });

export type GroupAccessOwnerViewPayloadActions = IColumnsConstructed | IFilterablesConstructed | IPeopleFetchedAction;

export interface IColumnsConstructed extends IBaseAction {
	type: GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FINISHED;
	columns: IGroupAccessOwnerColumn[];
}

export interface IFilterablesConstructed extends IBaseAction {
	type: GroupAccessOwnerViewActionTypes.FILTERABLE_CONSTRUCTION_FINISHED;
	filterables: IFilterable[];
}

export interface IPeopleFetchedAction extends IBaseAction {
	type: GroupAccessOwnerViewActionTypes.PEOPLE_FETCHED;
	people: IPage<IPerson>;
}

export interface IBaseAction extends Action {
	type: GroupAccessOwnerViewActionTypes;
	forView: string;
}

interface INoOpAction extends Action {
	type: GroupAccessOwnerViewActionTypes.NO_OP;
}

export interface IBaseViewScope {
	forHris: string;
}

export type TThunkGroupAccessOwnerViewAction<R> = TThunkAction<R, GroupAccessOwnerViewActions>;

export interface IGroupAccessOwnerViewActionCreators<TScope extends IBaseViewScope> {
	constructColumns: (scope: TScope, forView: string) => TThunkGroupAccessOwnerViewAction<void>;
	constructFilterables: (scope: TScope, forView: string) => TThunkGroupAccessOwnerViewAction<void>;
	queryForOwners: (query: IQueryOptions, scope: TScope, forView: string) => TThunkGroupAccessOwnerViewAction<void>;
}

export const reducer: Reducer<IGroupAccessOwnerViews> = (
	state: IGroupAccessOwnerViews | undefined,
	incomingAction: GroupAccessOwnerViewActions | INoOpAction,
) => {
	if (!state) {
		return {};
	}

	if (IsAGroupAccessOwnerViewAction(incomingAction)) {
		const { forView } = incomingAction;
		const currentStateOfView = state[forView] ?? DefaultViewState;

		return {
			...state,
			[incomingAction.forView]: ViewReducer(currentStateOfView, incomingAction),
		};
	} else {
		return state;
	}
};

export function IsAGroupAccessOwnerViewAction(action: Action): action is GroupAccessOwnerViewActions {
	const groupAccessOwnerActionTypes = Object.keys(GroupAccessOwnerViewActionTypes);

	return action.type !== GroupAccessOwnerViewActionTypes.NO_OP && groupAccessOwnerActionTypes.some((type) => type === action.type);
}

function ViewReducer(state: IGroupAccessOwnerViewState, action: GroupAccessOwnerViewActions): IGroupAccessOwnerViewState {
	switch (action.type) {
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_STARTED:
			return {
				...state,
				columns: { isLoading: true },
			};
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FINISHED:
			return {
				...state,
				columns: action.columns,
			};
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FAILED:
			return {
				...state,
				columns: { isLoading: false, couldNotFetchData: true },
			};
		case GroupAccessOwnerViewActionTypes.FILTERABLE_CONSTRUCTION_STARTED:
			return {
				...state,
				filterables: { isLoading: true },
			};
		case GroupAccessOwnerViewActionTypes.FILTERABLE_CONSTRUCTION_FINISHED:
			return {
				...state,
				filterables: action.filterables,
			};
		case GroupAccessOwnerViewActionTypes.REQUEST_PEOPLE:
			return {
				...state,
				people: { isLoading: true },
			};
		case GroupAccessOwnerViewActionTypes.PEOPLE_FETCHED:
			return {
				...state,
				people: action.people,
			};
		case GroupAccessOwnerViewActionTypes.PEOPLE_COULDNOT_BEFETCHED:
			return {
				...state,
				people: { isLoading: false, couldNotFetchData: true },
			};
		default:
			return state;
	}
}
